import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Contexts/AuthenticateProvider";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import ListLayout from "../../Components/List";
import VariantFormModal from "./VariantFormModal";
import useListParams from "../../Helpers/useListParams";

// Standalone Variants page (2026-09-16), backed by
// POST /staff/vehicle/variant/list/:page. Brand and model names are joined
// server-side. Picking a brand narrows the Model dropdown and clears any
// model selection that no longer belongs to it.
const INITIAL_FILTERS = { search: "", searchKey: "variantName", count: 10, brandId: "", modelId: "", status: "", sortBy: "createdAt", sort: false };

const SEARCH_KEYS = [{ key: "variantName", value: "Variant name" }];

const VariantList = () => {
    const { hasPermission } = useAuth();
    // Controls are hidden when the role lacks the capability - the router
    // enforces the same keys, this just keeps dead buttons off the page.
    const canCreate = hasPermission("vehicle.variant.create");
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: "/staff/vehicle/brand/active" });
            setBrands(response?.success ? response?.data?.data || [] : []);
        })();
    }, []);

    // Model dropdown follows the brand filter - no brand selected means every
    // active model across all brands.
    useEffect(() => {
        (async () => {
            const response = await Handler({
                method: "GET",
                url: "/staff/vehicle/model/active",
                params: filters.brandId ? { brandId: filters.brandId } : {},
            });
            setModels(response?.success ? response?.data?.data || [] : []);
        })();
    }, [filters.brandId]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/staff/vehicle/variant/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.brandId ? { brandId: filters.brandId } : {}),
                ...(filters.modelId ? { modelId: filters.modelId } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
                sortBy: filters.sortBy,
                sort: filters.sort,
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.brandId, filters.modelId, filters.status, filters.sortBy, filters.sort]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        // Changing brand invalidates any model picked under the old brand.
        setFilters((prev) => ({ ...prev, [key]: value, ...(key === "brandId" ? { modelId: "" } : {}) }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const filterItems = useMemo(() => ({
        dropdown: [
            {
                key: "brandId",
                name: "Brand",
                options: [{ key: "", name: "All" }, ...brands.map((b) => ({ key: b.brandId, name: b.brand }))],
            },
            {
                key: "modelId",
                name: "Model",
                options: [{ key: "", name: "All" }, ...models.map((m) => ({ key: m.modelId, name: m.modelName }))],
            },
            {
                key: "status",
                name: "Status",
                options: [{ key: "", name: "All" }, { key: true, name: "Active" }, { key: false, name: "Inactive" }],
            },
        ],
        sort: [
            { key: "variantName", name: "Name" },
            { key: "createdAt", name: "Created" },
        ],
    }), [brands, models]);

    const columns = [
        {
            key: "variantName",
            name: "Variant",
            mandatory: true,
            render: (row) => (
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex flex-column">
                        <span className="fw-semibold text-dark text-capitalize">{row.variantName || ""}</span>
                    </div>
                </div>
            ),
        },
        {
            key: "brand",
            name: "Brand",
            render: (row) => (
                <div className="small">
                    <div className="text-dark text-capitalize">{row.brand || ""}</div>
                </div>
            ),
        },
        {
            key: "modelName",
            name: "Model",
            render: (row) => (
                <div className="small">
                    <div className="text-dark text-capitalize">{row.modelName || ""}</div>
                </div>
            ),
        },
        {
            key: "segment",
            name: "Segment",
            render: (row) => (
                <div className="small">
                    <div className="text-dark text-capitalize">{[row.segment, row.subSegment].filter(Boolean).join(" / ")}</div>
                </div>
            ),
        },
        {
            key: "status",
            name: "Status",
            render: (row) => {
                const isActive = row.status === true;
                return (
                    <span className={`badge rounded-pill ${isActive ? "text-bg-success" : "text-bg-danger"}`}>
                        <i className={`bi ${isActive ? "bi-check-circle" : "bi-x-circle"}`}></i>
                        {isActive ? "Active" : "Inactive"}
                    </span>
                );
            },
        },
        {
            key: "createdAt",
            name: "Created",
            render: (row) => (
                <div className="small">
                    <div className="text-dark">{row.createdAt ? formatDateTime(row.createdAt) : ""}</div>
                </div>
            ),
        },
    ];

    return (
        <>
            <ListLayout
                title="Variants"
                description="Trim levels across every model - the bottom of the Brand -> Model -> Variant catalog."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by variant name..."
                onRowClick={(row) => navigate(`/variant/${row.variantId}`)}
                addList={canCreate ? () => setShowAddModal(true) : undefined}
            />

            {showAddModal ? (
                <VariantFormModal
                    mode="create"
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default VariantList;
